/**
 * UserAnnotation store — single source of truth for every persisted
 * annotation across the viewer.
 *
 * Backed by a TanStack DB collection (`userAnnotationCollection`), so
 * any number of subscribers — the modal panel, every stream card —
 * share the same row set and re-render in lockstep when it mutates.
 *
 * Reads (reactive, via `useLiveQuery`):
 *   userAnnotationStore.useByBlock(blockId)         — rows for one block
 *   userAnnotationStore.useByBlockIds(blockIds)     — rows for many blocks
 *
 * Imperative reads (no subscription):
 *   userAnnotationStore.getByBlock(blockId)         — current snapshot
 *
 * Loading:
 *   userAnnotationStore.ensureLoadedForBlocks(ids)  — batched prefetch
 *
 * Writes (optimistic via collection.utils.write*):
 *   userAnnotationStore.create(input)               — insert
 *   userAnnotationStore.update(id, patch)           — upsert
 *   userAnnotationStore.remove(id)                  — delete
 */
import { useMemo } from "react";
import { useLiveQuery, eq } from "@tanstack/react-db";
import { MAX_USER_ANNOTATION_BATCH_BLOCK_IDS, } from "@shared/types/user-annotation";
import { generateId, } from "@shared/types/id";
import { createUserAnnotation, deleteUserAnnotation, updateUserAnnotation, } from "@/api/command-api";
import { listUserAnnotationsByBlockIds } from "@/api/query-api";
import { userAnnotationCollection } from "@/data/user-annotation-collection";
import { OPTIMISTIC_OWNER_PLACEHOLDER } from "@/data/user-annotation-optimistic-id";
import { accountAttributionStore } from "@/data/account-attribution-store";
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
/**
 * Reactive list of UserAnnotations on a single block. `blockId` may be
 * `undefined` (e.g. while the modal is closed); the live query then
 * filters on an unmatchable empty string and returns no rows so the
 * caller can render `data ?? []` without a guard.
 */
function useByBlock(blockId) {
    const { data, isLoading } = useLiveQuery((q) => q
        .from({ a: userAnnotationCollection })
        .where(({ a }) => eq(a.blockId, blockId ?? "")), [blockId]);
    return { data: data ?? [], isLoading };
}
/**
 * Reactive list across multiple blocks. Returns the merged set; callers
 * group by `blockId` themselves when they need per-block lists. Used by
 * any future surface that wants annotations across many cards in one
 * subscription (e.g. an "annotations summary" view); each individual
 * stream card uses `useByBlock` directly.
 */
function useByBlockIds(blockIds) {
    // Stable key for the dep array — comparing the array reference would
    // re-subscribe on every parent render even when the ids haven't moved.
    const idsKey = blockIds.join("|");
    const { data, isLoading } = useLiveQuery((q) => {
        const set = new Set(blockIds);
        return q
            .from({ a: userAnnotationCollection })
            .where(({ a }) => set.has(a.blockId));
    }, [idsKey]);
    return { data: data ?? [], isLoading };
}
/**
 * Read the current snapshot for a block without subscribing. Walks the
 * collection's keys; cheap for the small per-block counts we expect.
 */
function getByBlock(blockId) {
    const out = [];
    for (const value of userAnnotationCollection.values()) {
        const row = value;
        if (row.blockId === blockId)
            out.push(row);
    }
    return out;
}
// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------
const inFlightLoads = new Set();
/**
 * Returns once the collection has finished its initial sync and is in
 * `'ready'` state. The TanStack DB `utils.write*` methods throw
 * `SyncNotInitializedError` when called any earlier, so any module-
 * level write needs to gate on this. When the collection is already
 * ready, this resolves on the next microtask via `Promise.resolve()`.
 *
 * Re-thrown errors are intentional — caller's `try/catch` decides
 * whether the failure should bubble or be logged.
 */
async function waitUntilCollectionReady() {
    if (userAnnotationCollection.status === "ready")
        return;
    // `stateWhenReady()` resolves once the collection's first sync
    // completes; we don't need its return value, just the readiness.
    await userAnnotationCollection.stateWhenReady();
}
/**
 * Ensure the collection is primed for `blockIds`. Skips ids whose block
 * already has at least one row in the collection (so re-paginating the
 * stream doesn't refetch annotations for blocks that are already
 * cached). Concurrent calls are deduplicated per block id.
 *
 * The batched call returns `[]` for empty inputs without hitting the
 * network, so this is safe to call for every page change.
 */
async function ensureLoadedForBlocks(blockIds) {
    if (blockIds.length === 0)
        return;
    // The collection's `utils.writeBatch` throws if called before the
    // first sync completes. Block-store fires this from a `useEffect`
    // that can run earlier than the user-annotation queryFn's first
    // resolve, so wait the writes out behind readiness.
    await waitUntilCollectionReady();
    // Build the set of already-cached block ids in one pass over the
    // collection's values (cheaper than the per-id `getByBlock` above
    // when batch sizes get into the dozens).
    const cachedBlockIds = new Set();
    for (const value of userAnnotationCollection.values()) {
        cachedBlockIds.add(value.blockId);
    }
    const missing = [];
    for (const id of blockIds) {
        if (cachedBlockIds.has(id))
            continue;
        if (inFlightLoads.has(id))
            continue;
        missing.push(id);
    }
    if (missing.length === 0)
        return;
    for (const id of missing)
        inFlightLoads.add(id);
    try {
        // Server hard-caps `/user-annotation/by-blocks` request size at
        // `MAX_USER_ANNOTATION_BATCH_BLOCK_IDS`. Chunk the miss set so
        // large collection views (where many blocks have zero annotations
        // and therefore stay "missing") still load incrementally instead
        // of failing the whole warm pass.
        const batchPromises = [];
        for (let start = 0; start < missing.length; start += MAX_USER_ANNOTATION_BATCH_BLOCK_IDS) {
            const batch = missing.slice(start, start + MAX_USER_ANNOTATION_BATCH_BLOCK_IDS);
            batchPromises.push(listUserAnnotationsByBlockIds(batch));
        }
        const batchResults = await Promise.allSettled(batchPromises);
        let firstBatchError = undefined;
        for (const batchResult of batchResults) {
            if (batchResult.status === "fulfilled") {
                const rows = batchResult.value;
                userAnnotationCollection.utils.writeBatch(() => {
                    for (const row of rows) {
                        userAnnotationCollection.utils.writeUpsert(row);
                    }
                });
                continue;
            }
            if (firstBatchError === undefined) {
                firstBatchError = batchResult.reason;
            }
        }
        if (firstBatchError !== undefined) {
            throw firstBatchError;
        }
    }
    catch (e) {
        // Surface the failure for observability; don't throw — the stream
        // continues to work with whatever's already cached, and the next
        // ensureLoadedForBlocks call will retry the now-untracked ids.
        console.warn("[userAnnotationStore.ensureLoadedForBlocks] failed:", e);
    }
    finally {
        for (const id of missing)
            inFlightLoads.delete(id);
    }
}
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
/**
 * Reconcile an optimistic row against the server's response.
 *
 * Two policies layered on top of the server response:
 *
 * 1. Schema-skew defense (legacy) — when a client sends a field the
 *    server doesn't yet understand, Zod strips it before persisting
 *    and the response comes back missing the field. Affected fields
 *    today:
 *      - `anchor` (Phase 2 unification, panel_point on stamp /
 *        sticker / etc.)
 *      - `content.transform.{naturalWidth, naturalHeight}` (drawings,
 *        introduced with outlined-stroke scaling)
 *      - `content.style` (stamps, introduced with emoji reactions)
 *    For each, fall back to the optimistic value when the server
 *    returned undefined.
 *
 * 2. Gesture endpoint preservation (jump-back mitigation) — for
 *    `anchor` and the transform axes that gestures actively drive
 *    (width / height / rotation per type), prefer the optimistic
 *    value over the server's response unconditionally, not only
 *    when the server stripped the field. The optimistic row IS
 *    the user's drag endpoint at the moment they released, and
 *    it's already painted on screen via the `writeOptimistic`
 *    writes that ran during the gesture. If the server's PATCH
 *    response differs (e.g. a Hyperdrive read-after-write
 *    returning the pre-update row from a 60s SELECT cache, an
 *    older server stripping a field on the round-trip, or any
 *    other transient staleness), keep what the user just put on
 *    screen and let the next explicit reload reconcile any
 *    genuine server-side adjustments.
 *
 *    TODO(api-staleness): the underlying issue is in
 *    `production/api/src/domain/user-annotation/repository.ts`'s
 *    `update` path — `updateMany` + a separate `findFirst`
 *    exposes the read-back to Hyperdrive's SELECT cache, which
 *    is what makes "drag, release, jump back to original" visible
 *    to users. Switching the repo to
 *    `prisma.userAnnotation.update({ where: { id }, data })` (a
 *    single `UPDATE … RETURNING *` statement) makes the read-back
 *    cache-invariant. Once that lands, the gesture-axis override
 *    here can be relaxed back to the narrower "server returned
 *    undefined" rule and this comment block simplified.
 *
 * Everything else (`id`, `updatedAt`, `ownerId`, `createdAt`,
 * `parentId`, content fields outside the gesture axes) flows from
 * the server unchanged.
 */
function reconcileUpdated(optimistic, server) {
    if (optimistic.type !== server.type)
        return server;
    let result = server;
    // Anchor: optimistic wins whenever it's defined. Covers both the
    // legacy schema-strip case (server returns undefined → we restore)
    // and the staleness case (server returns a different anchor → we
    // keep the user's drag endpoint).
    if (optimistic.anchor !== undefined) {
        result = { ...result, anchor: optimistic.anchor };
    }
    if (optimistic.type === "drawing" && result.type === "drawing") {
        const opt = optimistic.content.transform;
        const srv = result.content.transform;
        result = {
            ...result,
            content: {
                ...result.content,
                transform: {
                    ...srv,
                    // Gesture-driven axes — optimistic wins outright.
                    width: opt.width,
                    height: opt.height,
                    rotation: opt.rotation,
                    // Natural dimensions are frozen at create and never
                    // change in a transform PATCH; backfill from optimistic
                    // only when the server stripped them.
                    naturalWidth: srv.naturalWidth ?? opt.naturalWidth,
                    naturalHeight: srv.naturalHeight ?? opt.naturalHeight,
                },
            },
        };
    }
    if (optimistic.type === "sticker" && result.type === "sticker") {
        const opt = optimistic.content.transform;
        const srv = result.content.transform;
        result = {
            ...result,
            content: {
                ...result.content,
                transform: {
                    ...srv,
                    width: opt.width,
                    height: opt.height,
                    rotation: opt.rotation,
                },
            },
        };
    }
    if (optimistic.type === "stamp" && result.type === "stamp") {
        const opt = optimistic.content.transform;
        const srv = result.content.transform;
        // Capture `style` BEFORE the reassignment below — once `result`
        // is widened back to `UserAnnotation` via the assertion cast,
        // TypeScript loses the `result.type === "stamp"` narrowing and
        // can't resolve `result.content.style` against the union.
        const optStyle = optimistic.content.style;
        const srvStyle = result.content.style;
        // Stamps persist height + rotation in transform (width is
        // intrinsic to the rendered text); both axes are gesture-driven.
        result = {
            ...result,
            content: {
                ...result.content,
                transform: {
                    ...srv,
                    height: opt.height,
                    rotation: opt.rotation,
                },
            },
        };
        // `style` isn't gesture-driven — keep the original
        // schema-skew rule (optimistic only when server returned
        // undefined) so a server that genuinely changes the style
        // still wins. Pulled the reads above for the narrowing reason
        // documented at the top of this branch.
        if (optStyle !== undefined && srvStyle === undefined) {
            result = {
                ...result,
                content: { ...result.content, style: optStyle },
            };
        }
    }
    return result;
}
/**
 * Build an optimistic UserAnnotation row that satisfies the discriminated
 * union shape. Server returns the canonical row on success and we
 * upsert it in place (same id), so this is just the placeholder bridge
 * between `create()` returning a Promise and the UI re-rendering with a
 * real row in the next React commit.
 */
function optimisticAnnotation(input) {
    const now = new Date().toISOString();
    // Phase 2: anchor lives on every type, so the optimistic row is a
    // single shape regardless of variant. The discriminated `(type,
    // content)` correlation is preserved at the input boundary, and
    // the cast at the bottom bridges it back to the discriminated
    // `UserAnnotation` union.
    return {
        id: generateId("userAnnotation"),
        blockId: input.blockId,
        ownerId: OPTIMISTIC_OWNER_PLACEHOLDER,
        parentId: input.parentId
            ? input.parentId
            : undefined,
        anchor: input.anchor,
        type: input.type,
        content: input.content,
        createdAt: now,
        updatedAt: now,
        deletedAt: undefined,
    };
}
/**
 * Create a UserAnnotation. Inserts an optimistic row, fires the server
 * call, and reconciles by replacing the optimistic id with the real
 * server-issued row. On failure the optimistic row is removed.
 */
async function create(input) {
    // The collection's writes throw before its first sync completes.
    // User-driven creates should never realistically race the initial
    // queryFn (the toolbar that triggers them mounts well after the
    // page loads), but the guard is cheap and covers the case where a
    // very early test or a hot-reload re-fires creation.
    await waitUntilCollectionReady();
    const optimistic = optimisticAnnotation(input);
    userAnnotationCollection.utils.writeInsert(optimistic);
    try {
        const created = await createUserAnnotation(input);
        // Defensive: if the server stripped fields the client sent (e.g.
        // a stamp's `transform.x / .y` against a server schema that
        // hasn't deployed those fields yet), prefer the optimistic
        // content over the stripped one. See `reconcileUpdated` for the
        // full rationale.
        const reconciled = reconcileUpdated(optimistic, created);
        // Capture the server-issued `ownerId` as the current user's
        // accountId. The row we just created is necessarily owned by us,
        // so its `ownerId` IS our internal Account.id. This is the
        // backstop path for `useCurrentAccountId` — the priming-from-
        // members path covers the typical flow (open a block whose
        // collection lists the user as a member), but for users who
        // haven't loaded any such members list yet, the first successful
        // create populates the slot. Idempotent — `setCurrentAccountId`
        // is a no-op when the slot already matches.
        accountAttributionStore.getState().setCurrentAccountId(reconciled.ownerId);
        userAnnotationCollection.utils.writeBatch(() => {
            // Drop the optimistic id and insert the canonical row. If the
            // server happened to assign the same id (e.g. the stub branch
            // does this in dev) the upsert below collapses to a single row.
            userAnnotationCollection.utils.writeDelete(optimistic.id);
            userAnnotationCollection.utils.writeUpsert(reconciled);
        });
        return reconciled;
    }
    catch (error) {
        userAnnotationCollection.utils.writeDelete(optimistic.id);
        throw error;
    }
}
/**
 * Build the next-state row for an optimistic update by walking the
 * patch deltas. Returns a fresh row object — the caller decides
 * whether to upsert it into the collection or use it for validation.
 *
 * Mirrors the entity's `buildUpdatePatch` discipline: one place for
 * the patch logic, no risk of "candidate to validate" disagreeing
 * with "patch to write" since both flow from the same delta walk.
 */
function applyAnnotationPatch(previous, patch) {
    const next = { ...previous };
    if (patch.content !== undefined)
        next.content = patch.content;
    if (patch.clearParentId === true) {
        next.parentId = undefined;
    }
    else if (patch.parentId !== undefined) {
        next.parentId = patch.parentId;
    }
    // Phase 2: anchor lives on every type, no comment-only branch.
    if (patch.clearAnchor === true) {
        next.anchor = undefined;
    }
    else if (patch.anchor !== undefined) {
        next.anchor = patch.anchor;
    }
    next.updatedAt = new Date().toISOString();
    return next;
}
/**
 * Apply an optimistic patch and reconcile with the server's response.
 * The collection's primary key is the annotation `id` alone — no
 * `blockId` parameter is needed at this layer.
 */
async function update(id, patch) {
    await waitUntilCollectionReady();
    const previous = userAnnotationCollection.get(id);
    if (!previous) {
        return updateUserAnnotation(id, patch);
    }
    // Variant mismatch: skip the optimistic patch (the server will reject
    // anyway) and let the round-trip return the validation error.
    if (previous.type !== patch.type) {
        return updateUserAnnotation(id, patch);
    }
    const next = applyAnnotationPatch(previous, patch);
    userAnnotationCollection.utils.writeUpsert(next);
    try {
        const updated = await updateUserAnnotation(id, patch);
        // Defensive: if the server stripped a field the client sent (e.g.
        // an older server still on the comment-only-anchor schema strips
        // a stamp's `panel_point` anchor), prefer the locally-patched
        // value over the server's regressed one. See `reconcileUpdated`
        // for the narrow contract — anchor is the only field reconciled.
        const reconciled = reconcileUpdated(next, updated);
        // Don't resurrect a row the user deleted while the PATCH was
        // in flight. If the row is no longer in the local collection,
        // a concurrent `remove(id)` ran during the await — writing
        // back the reconciled row would undo the user's intentional
        // delete. Skip the upsert and let the eventual server-side
        // soft-delete reconcile remote state.
        if (userAnnotationCollection.get(id) !== undefined) {
            userAnnotationCollection.utils.writeUpsert(reconciled);
        }
        return reconciled;
    }
    catch (error) {
        // Same deletion-race guard for the rollback path: only restore
        // `previous` if the row still exists locally. A 404 here is
        // the expected response when DELETE arrived first on the
        // server, and rolling back would resurrect the deleted row.
        if (userAnnotationCollection.get(id) !== undefined) {
            userAnnotationCollection.utils.writeUpsert(previous);
        }
        throw error;
    }
}
/**
 * Soft-delete a UserAnnotation. Optimistically removes the row, rolls
 * back on failure.
 */
async function remove(id) {
    await waitUntilCollectionReady();
    const previous = userAnnotationCollection.get(id);
    if (previous) {
        userAnnotationCollection.utils.writeDelete(id);
    }
    try {
        await deleteUserAnnotation(id);
    }
    catch (error) {
        if (previous)
            userAnnotationCollection.utils.writeUpsert(previous);
        throw error;
    }
}
/**
 * Soft-delete a comment AND any sibling `text_highlight` annotations
 * anchored to the same `text_range` on the same block.
 *
 * Cascade scope: only top-level comments (no `parentId`) cascade —
 * a reply shares the parent's anchor, but the highlight is
 * conceptually owned by the thread root, not by individual replies.
 * Deleting a reply leaves the highlight (and the parent it belongs
 * to) intact.
 *
 * Anchor match: exact `(start, end)` equality on a `text_range`
 * selector. The `quote` is intentionally not part of the match —
 * it's a fuzzy fallback for offset drift, not part of the row's
 * identity. Two highlights with the same `(start, end)` would
 * compose to the same visible span, so cascading both is the right
 * read of the user's "remove this annotated span" intent.
 *
 * Falls back to a plain `remove(id)` when the row isn't a top-level
 * `text_range` comment so this helper can be the single delete
 * entry point for every comment surface (rail row, popover row,
 * popover reply row) without each caller re-deriving the cascade
 * eligibility.
 */
async function removeCommentWithCascade(id) {
    await waitUntilCollectionReady();
    const comment = userAnnotationCollection.get(id);
    if (!comment ||
        comment.type !== "comment" ||
        comment.parentId !== undefined ||
        comment.anchor?.type !== "text_range") {
        await remove(id);
        return;
    }
    const anchor = comment.anchor;
    const blockIdStr = comment.blockId;
    // Walk the local snapshot once to find sibling highlights on the
    // same block whose anchor matches `(start, end)`. The collection
    // is small per-block so the linear scan is cheaper than building
    // an index; same scan shape `getByBlock` already uses.
    const siblingHighlightIds = [];
    for (const value of userAnnotationCollection.values()) {
        const row = value;
        if (row.type !== "text_highlight")
            continue;
        if (row.blockId !== blockIdStr)
            continue;
        if (row.anchor?.type !== "text_range")
            continue;
        if (row.anchor.start !== anchor.start)
            continue;
        if (row.anchor.end !== anchor.end)
            continue;
        siblingHighlightIds.push(row.id);
    }
    // Issue every delete in parallel — the comment + each sibling
    // highlight is independent at the API layer (no transactional
    // boundary between rows), so awaiting them serially would just
    // multiply the round-trip cost. Each underlying `remove` already
    // owns its own optimistic-write + rollback discipline, so a
    // failure on any one row is contained.
    const ids = [id, ...siblingHighlightIds];
    await Promise.all(ids.map((rowId) => remove(rowId)));
}
// ---------------------------------------------------------------------------
// Cache management
// ---------------------------------------------------------------------------
/**
 * Drop every row in the collection. Called from `auth-context.tsx` on
 * user-id transitions (sign-out, sign-in as a different user) so
 * optimistic placeholders or stale rows from another account never
 * bleed into the next session's UI. The next `ensureLoadedForBlocks`
 * call (typically after the stream settles for the new user) repopulates
 * from the server.
 *
 * Auth fires its initial subscribe synchronously on mount, which can
 * land *before* the collection's first sync completes. The TanStack
 * DB `utils.writeBatch` throws `SyncNotInitializedError` in that
 * window, so guard on readiness — there are no rows to clear when
 * the collection hasn't synced yet, and the upcoming first sync will
 * fetch under the new auth context anyway. We also reset the
 * in-flight loads tracker unconditionally because that lives in our
 * own module state, independent of the collection's readiness.
 */
function clearAllCaches() {
    inFlightLoads.clear();
    if (userAnnotationCollection.status !== "ready")
        return;
    const ids = [];
    for (const value of userAnnotationCollection.values()) {
        ids.push(value.id);
    }
    if (ids.length === 0)
        return;
    userAnnotationCollection.utils.writeBatch(() => {
        for (const id of ids)
            userAnnotationCollection.utils.writeDelete(id);
    });
}
// ---------------------------------------------------------------------------
// Hook helpers
// ---------------------------------------------------------------------------
/**
 * Convenience wrapper for stream callers that want a deduped, sorted-
 * by-createdAt list for one block. The base `useByBlock` returns rows
 * in collection-iteration order; this stabilizes them so render order
 * is deterministic across re-mounts.
 */
function useByBlockSorted(blockId) {
    const { data, isLoading } = useByBlock(blockId);
    const sorted = useMemo(() => {
        return [...data].sort((a, b) => a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0);
    }, [data]);
    return { data: sorted, isLoading };
}
/**
 * Write a fully-formed UserAnnotation row directly into the local
 * collection without hitting the network. Used by gesture handlers
 * that need to render at 60 fps (drag / resize / rotate) where every
 * frame's network call would be wasteful — the gesture writes
 * locally, and the gesture's `onSettle` calls `update()` once to
 * persist.
 *
 * Treat this as the lower-level escape hatch for `update()`. The
 * caller is responsible for keeping the row's `id` and `blockId`
 * stable and for eventually persisting via `update()` so the row
 * isn't only local. If the collection isn't ready yet, the call is
 * a no-op (the gesture is happening before the first sync, which
 * realistically can't happen — the modal that hosts the gesture
 * mounts well after the page loads — but the guard is cheap).
 */
function writeOptimistic(row) {
    if (userAnnotationCollection.status !== "ready")
        return;
    userAnnotationCollection.utils.writeUpsert(row);
}
export const userAnnotationStore = {
    useByBlock: useByBlockSorted,
    useByBlockIds,
    getByBlock,
    ensureLoadedForBlocks,
    create,
    update,
    remove,
    removeCommentWithCascade,
    writeOptimistic,
    clearAllCaches,
};
